import axios from 'axios'
import { encryptText, decryptText } from '../common/crypto' // Import your encryption/decryption functions
import { USER_LOGOUT } from './types'

const config = process.env

const ENABLE_ENCRYPTION = config.REACT_APP_ENABLE_ENCRYPTION === 'true'
console.log('ENABLE_ENCRYPTION', ENABLE_ENCRYPTION)
console.log('REACT_APP_ENABLE_ENCRYPTION', config.REACT_APP_ENABLE_ENCRYPTION)

// Axios parses JSON responses automatically, so an encrypted response body
// `{"encryptedData":"..."}` arrives as an object, not the raw ciphertext string
// decryptText() expects. Fetch-based calls get the raw text instead, so parse
// that first. Unwrap either shape down to the ciphertext string.
const unwrapEncryptedData = (raw) => {
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed?.encryptedData ?? raw
    } catch (error) {
      return raw
    }
  }
  return raw?.encryptedData ?? raw
}
const API_BASE_URL = localStorage.getItem('selectUrl') || config.REACT_APP_END_POINT // API base URL
const API_CENTRAL_URL = config.REACT_APP_CENTRAL_BASE_URL // Central base URL
export const getApiUrl = () =>
  localStorage.getItem('selectUrl') || config.REACT_APP_END_POINT


const apiService = axios.create({
  baseURL: API_BASE_URL,
})

const apiWhoService = axios.create({
  baseURL: API_CENTRAL_URL,
})

const SESSION_ENDED_STORAGE_KEY = 'sessionEndedMessage'
const DEFAULT_SESSION_ENDED_MESSAGE =
  'Your session has ended because your account was logged in from another browser or device.'

// A 401 with reason: 'SESSION_TERMINATED' means the backend's per-request
// admin_tokens check (see controller/auth.js verifyHeader) found this
// device's token was evicted by a newer login elsewhere -- not a routine
// expired-JWT 401. Wired once here, globally, rather than in every action
// that calls Post/Get/Put/Delete.
apiService.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      let data = error.response.data
      if (ENABLE_ENCRYPTION && data) {
        try {
          data = JSON.parse(await decryptText(unwrapEncryptedData(data)))
        } catch (decryptErr) {
          // Fall through with the raw (undecryptable) body -- reason check
          // below just won't match, and the 401 still rejects normally.
        }
      }

      if (data?.reason === 'SESSION_TERMINATED') {
        sessionStorage.setItem(
          SESSION_ENDED_STORAGE_KEY,
          data?.message || DEFAULT_SESSION_ENDED_MESSAGE,
        )
        // Lazy require avoids a circular import at module-load time (this
        // file is imported by the actions the store's reducers are built
        // from); store.dispatch is only ever called once the interceptor
        // actually fires, by which point the store module is fully loaded.
        const { store } = require('./store')
        store.dispatch({ type: USER_LOGOUT })
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  },
)

// Function to set custom headers
const setHeaders = (headers) => {
  apiService.defaults.headers.common = {
    ...apiService.defaults.headers.common,
    ...headers,
  }
}

// Create operation
export const Post = async (url, itemData, user, ContentType='application/json', options = {}) => {
  try {
    setHeaders({
      Accept: 'application/json',
      'Content-Type': ContentType,
      Authorization: `Bearer ${user?.token}`,
    })
    // Encrypt itemData before sending
    let encryptedData = ENABLE_ENCRYPTION ? await encryptText(JSON.stringify(itemData)) : itemData

    const payload = ENABLE_ENCRYPTION ? { encryptedData } : itemData;

    const response = await apiService.post(`${url}`, payload, {
      signal: options.signal, // Support AbortController signal
    })

    const data = ENABLE_ENCRYPTION ? await decryptText(unwrapEncryptedData(response.data)) : response.data

    return { success: true, data }
  } catch (error) {
    // Check if request was cancelled
    if (axios.isCancel(error) || error.name === 'CanceledError') {
      return {
        success: false,
        cancelled: true,
        error: { message: 'Request cancelled' },
      }
    }

    // Safely handle cases where error.response is undefined
    const rawErrorData = error?.response?.data
    let errorData
    if (rawErrorData) {
      errorData = ENABLE_ENCRYPTION
        ? await decryptText(unwrapEncryptedData(rawErrorData))
        : rawErrorData
    } else {
      errorData = { message: error?.message || 'Unknown error occurred' }
    }

    return { success: false, error: errorData }
  }
}

// File upload operation (for FormData)
export const PostFile = async (url, formData, user, options = {}) => {
  try {
    const response = await apiService.post(`${url}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${user?.token}`,
        ...options.headers
      },
      ...options
    })

    const data = ENABLE_ENCRYPTION ? await decryptText(unwrapEncryptedData(response.data)) : response.data

    return { success: true, data }
  } catch (error) {
    const errorData = ENABLE_ENCRYPTION && error.response?.data ? await decryptText(unwrapEncryptedData(error.response.data)) : error.response?.data
    throw { success: false, error: errorData, message: error.message }
  }
}

// Read operation
export const Get = async (url, user) => {
  try {
    setHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user?.token}`,
    })
    const response = await apiService.get(`${url}`)

    const data = ENABLE_ENCRYPTION ? await decryptText(unwrapEncryptedData(response.data)) : response.data

    return { success: true, data }

  } catch (error) {
    const errorData = ENABLE_ENCRYPTION ? await decryptText(unwrapEncryptedData(error.response.data)) : error.response.data
    return { success: false, error: errorData }
  }
}

// Update operation
export const Put = async (url, itemData, user) => {
  try {
    setHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user?.token}`,
    })

    // Encrypt itemData before sending
    const encryptedData = ENABLE_ENCRYPTION ? await encryptText(JSON.stringify(itemData)) : itemData

    const payload = ENABLE_ENCRYPTION ? { encryptedData } : itemData;

    const response = await apiService.put(`${url}`, payload)

    // Decrypt response data
    const data = ENABLE_ENCRYPTION ? await decryptText(unwrapEncryptedData(response.data)) : response.data

    return { success: true, data }
  } catch (error) {
    const errorData = ENABLE_ENCRYPTION ? await decryptText(unwrapEncryptedData(error.response.data)) : error.response.data

    return { success: false, error: errorData }
  }
}

// Read WHO operation
export const GetWho = async (url) => {
  try {
    const response = await apiWhoService.get(`${url}`)

    // Decrypt response data
    const decryptedData = await decryptText(unwrapEncryptedData(response.data))

    return decryptedData
  } catch (error) {
    throw error
  }
}
export const GetWhoStr = async (url) => {
  try {
    const response = await apiWhoService.get(`${url}`)

    // Decrypt response data
    // const decryptedData = await decryptText(response.data)

    return response
  } catch (error) {
    throw error
  }
}

// Delete operation
export const Delete = async (url, user) => {
  try {
    setHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${user?.token}`,
    })

    const response = await apiService.delete(`${url}`)

    // Decrypt response data
    const decryptedData = await decryptText(unwrapEncryptedData(response.data))

    return decryptedData
  } catch (error) {
    throw error
  }
}

export default apiService

export const PostBlob = async (url, itemData, user) => {
  try {
    // Encrypt itemData if enabled
    const encryptedData = ENABLE_ENCRYPTION
      ? await encryptText(JSON.stringify(itemData))
      : null;

    const payload = ENABLE_ENCRYPTION
      ? JSON.stringify({ encryptedData })
      : JSON.stringify(itemData);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user?.token}`,
      },
      body: payload,
    });

    if (!response.ok) {
      const errorText = await response.text();
      const decryptedError = ENABLE_ENCRYPTION
        ? await decryptText(unwrapEncryptedData(errorText))
        : errorText;
      return { success: false, error: decryptedError };
    }

    const responseText = await response.text();
    const decryptedData = ENABLE_ENCRYPTION
      ? await decryptText(unwrapEncryptedData(responseText))
      : responseText;

    return { success: true, data: decryptedData };
  } catch (err) {
    return { success: false, error: err.message || 'Unknown error occurred' };
  }
};
